// 主なき近衛騎士の石突 (lanceButt・床の罠 buttTrap) と突きの当たり (lance_flash) の光 — 比較版 (2026-10-08)。
//
// ユーザーの選択 (10-08・docs/research/2026-10-08-knight-fx-references.md §3):
//   K-1 石突 = 「A 割れ目が走って噴く」 → `?knightButtFx=crack`
//   K-2 突き = 「A 接点の星＋火花」     → `?knightThrustFx=star`
//   10-08 検収で両方「新しい方が良い」＝配布版の既定。旧 (render.ts の buttTrap 分岐・drawLanceFlash) は DEV の now で見られる。
//
// 描くだけ。当たり・時間・ダメージは変えない (buttTrap は帯の全体が当たりの 0ms から有効のまま)。
//   そのため割れ目の「走る」部分は約2コマ (RUN_MS) で帯へ届き、帯の噴き上がりは当たりの 0ms から立ち上がる (調べ §3 の逃げ道 a)。
//
// 縛り: 線1本・正円・細長い光の筋・同じ大きさの図形の等間隔の並びは使わない。材料は粒と面。
//   色 = 騎士側に紫を使わない (主人公の魂)。赤黒い煙 = 弾けない攻撃の予兆 (全ボス共通)。金 = パリィ (使わない)。
//   突きの光は暖かい白〜朱。冷たい青白は使わない (render.ts の騎士の色の決まり)。
import type { ActiveHit } from './types'
import { ARENA } from './balance'
import { glowMote, hash01 } from './fx_kit'
import { blurPx } from './render_scale'

export type KnightButtFx = 'now' | 'crack' | 'crack1' | 'crack3' | 'rift'
export type KnightThrustFx = 'now' | 'star'

// 2026-10-08 ユーザー採用 (kensyu-knight-fx2 の K-1・K-2「新しい方が良い」) = 突きの既定は star。
// 石突は同日 kensyu-knight-butt の K-1「新しい方が良い」・K-2「合っている」・T-1〜T-3 全部「はい」で 3版 (帯の中に収めた暗い石畳) が既定。
//   旧の光は DEV `?knightButtFx=now` / `?knightThrustFx=now`、2版 (帯の外へはみ出す) は `?knightButtFx=crack`、1版は `crack1`。
// 2026-10-20 床技7つの見た目の選択 (C「走るひび」・噴き出す物は結晶でなく石と砂ぼこり) = `rift` が既定。
//   描いた部品の絵 (Codex・public/sprites/fx/hazard/knight_butt_rift_atlas.png) をコードで動かす。10-08 の3版は DEV `?knightButtFx=crack3`。
let buttFx: KnightButtFx = 'rift'
let thrustFx: KnightThrustFx = 'star'

/** game.ts の DEV URL ブロックだけが呼ぶ。指定なし (null) は採用済みの既定。 */
export function setKnightLanceFx(butt: string | null, thrust: string | null): void {
  buttFx = butt === 'now' ? 'now' : butt === 'crack1' ? 'crack1' : butt === 'crack' ? 'crack' : butt === 'crack3' ? 'crack3' : 'rift'
  thrustFx = thrust === 'now' ? 'now' : 'star'
}
export function getKnightButtFx(): KnightButtFx { return buttFx }
export function getKnightThrustFx(): KnightThrustFx { return thrustFx }

// ---------------------------------------------------------------- 共通の小道具

function clamp01(v: number): number { return v < 0 ? 0 : v > 1 ? 1 : v }
function easeOut(p: number): number { const q = 1 - clamp01(p); return 1 - q * q * q }

// 柔らかい縁の楕円 (煙・土煙の一塊)。回転と縦横比を塊ごとに変え、正円に見せない。
function softBlob(
  ctx: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number, rot: number,
  color: string, a: number,
): void {
  if (a <= 0.01 || rx <= 0.5 || ry <= 0.5) return
  ctx.save()
  ctx.globalAlpha = a
  ctx.translate(x, y)
  ctx.rotate(rot)
  ctx.scale(1, ry / rx)
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, rx)
  g.addColorStop(0, color)
  g.addColorStop(0.45, color)
  g.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(0, 0, rx, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

// 欠けた石のかけら (3〜5角のいびつな多角形)。上の面だけ少し明るく塗って立体に見せる。
function stoneChip(
  ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rot: number, seed: number, a: number,
  dark = '#242129', light = '#77727f',
): void {
  if (a <= 0.01 || r <= 0.4) return
  const n = 3 + Math.floor(hash01(seed) * 3)
  ctx.save()
  ctx.globalAlpha = a
  ctx.translate(x, y)
  ctx.rotate(rot)
  ctx.beginPath()
  for (let i = 0; i < n; i += 1) {
    const ang = (i / n) * Math.PI * 2 + (hash01(seed + i * 1.7) - 0.5) * 0.9
    const rr = r * (0.55 + hash01(seed + i * 2.3) * 0.6)
    const px = Math.cos(ang) * rr, py = Math.sin(ang) * rr
    if (i === 0) ctx.moveTo(px, py)
    else ctx.lineTo(px, py)
  }
  ctx.closePath()
  ctx.fillStyle = dark
  ctx.fill()
  // 上の欠け面 (光を受ける側)
  ctx.beginPath()
  ctx.moveTo(-r * 0.7, -r * 0.15)
  ctx.lineTo(-r * 0.1, -r * 0.75)
  ctx.lineTo(r * 0.6, -r * 0.35)
  ctx.lineTo(r * 0.1, -r * 0.05)
  ctx.closePath()
  ctx.fillStyle = light
  ctx.globalAlpha = a * 0.75
  ctx.fill()
  ctx.restore()
}

// ---------------------------------------------------------------- K-1 割れ目が走って噴く

const FLOOR = ARENA.floorY
// 割れ目が石突の着点から帯の外縁まで走る時間。0ms で約半分・約33ms (2コマ) で届き切る。
const RUN_MS = 46
const RUN_LEAD_MS = 12

type ButtGeom = {
  buttX: number
  dir: 1 | -1          // 石突 → 帯 の向き
  inner: number        // 帯の石突に近い側の縁
  outer: number
  xStart: number
  xEnd: number
  cx: number
  w: number
  seed: number
}

function buttGeom(hz: ActiveHit, buttX: number): ButtGeom {
  const cx = (hz.xStart + hz.xEnd) / 2
  const dir: 1 | -1 = cx >= buttX ? 1 : -1
  return {
    buttX, dir, cx, w: hz.xEnd - hz.xStart, xStart: hz.xStart, xEnd: hz.xEnd,
    inner: dir > 0 ? hz.xStart : hz.xEnd, outer: dir > 0 ? hz.xEnd : hz.xStart,
    seed: hz.id * 1.37 + 11.3,
  }
}

// 割れ目の中心線 (床の面の上でうねる)。距離 s (石突から) を受けて y を返す。帯ごとに固定。
function crackY(g: ButtGeom, s: number): number {
  const k = s / 9
  const k0 = Math.floor(k), f = k - k0
  const a = hash01(g.seed + k0 * 3.11) - 0.5
  const b = hash01(g.seed + (k0 + 1) * 3.11) - 0.5
  return FLOOR + 4 + (a + (b - a) * f) * 10
}

// 割れ目の幅: 石突の近くで細く、帯へ近づくほど開く。ところどころで欠けて太る。
function crackHalf(g: ButtGeom, s: number, total: number): number {
  const u = clamp01(s / Math.max(1, total))
  const chip = hash01(g.seed + Math.round(s / 6) * 1.91)
  return 1.4 + u * 3.2 + (chip > 0.68 ? chip * 3.0 : chip * 0.8)
}

// 走る割れ目 (石突の着点 → 帯の内側の縁)。黒い欠けた面 + 縁の明るい欠け + 奥の鈍い赤。
function drawRunningCrack(ctx: CanvasRenderingContext2D, g: ButtGeom, front: number, fade: number): void {
  const total = Math.abs(g.inner - g.buttX) + g.w * 0.25
  const reach = total * front
  if (reach < 2) return
  const step = 5
  const n = Math.max(2, Math.ceil(reach / step))
  const top: Array<[number, number]> = []
  const bot: Array<[number, number]> = []
  for (let i = 0; i <= n; i += 1) {
    const s = Math.min(reach, i * step)
    const x = g.buttX + g.dir * s
    const y = crackY(g, s)
    const h = (i === 0 || i === n) ? 0.5 : crackHalf(g, s, total)
    top.push([x + (hash01(g.seed + i * 0.61) - 0.5) * 2, y - h])
    bot.push([x + (hash01(g.seed + i * 0.83) - 0.5) * 2, y + h * 0.8])
  }
  ctx.save()
  // 割れ目の下の影 (床が沈んだ面)
  ctx.globalAlpha = 0.45 * fade
  ctx.fillStyle = '#0b090e'
  ctx.beginPath()
  top.forEach(([x, y], i) => { if (i === 0) ctx.moveTo(x, y - 1.5); else ctx.lineTo(x, y - 1.5) })
  for (let i = bot.length - 1; i >= 0; i -= 1) ctx.lineTo(bot[i]![0], bot[i]![1] + 2.5)
  ctx.closePath()
  ctx.fill()
  // 割れ目そのもの = 欠けた石の黒い面
  ctx.globalAlpha = 0.95 * fade
  ctx.fillStyle = '#050407'
  ctx.beginPath()
  top.forEach(([x, y], i) => { if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y) })
  for (let i = bot.length - 1; i >= 0; i -= 1) ctx.lineTo(bot[i]![0], bot[i]![1])
  ctx.closePath()
  ctx.fill()
  // 上の縁の欠け (光を受けた石の面・ところどころだけ)
  for (let i = 1; i < top.length - 1; i += 1) {
    const hv = hash01(g.seed + i * 4.07)
    if (hv < 0.58) continue
    const [x0, y] = top[i]!
    const x = x0 + (hash01(g.seed + i * 5.13) - 0.5) * step
    const wv = 2 + hv * hash01(g.seed + i * 6.29) * 9
    ctx.globalAlpha = (0.35 + hv * 0.4) * fade
    ctx.fillStyle = '#8f8c9e'
    ctx.beginPath()
    ctx.moveTo(x - wv * 0.6, y + 0.3)
    ctx.lineTo(x - wv * 0.1, y - 1.8 - hv * 2.2)
    ctx.lineTo(x + wv * 0.7, y - 0.2)
    ctx.closePath()
    ctx.fill()
    // 下の縁は影の欠け
    const [bx, by] = bot[i]!
    if (hv > 0.6) {
      ctx.globalAlpha = 0.6 * fade
      ctx.fillStyle = '#17151c'
      ctx.beginPath()
      ctx.moveTo(bx - wv * 0.5, by - 0.3)
      ctx.lineTo(bx + wv * 0.2, by + 1.5 + hv * 1.5)
      ctx.lineTo(bx + wv * 0.6, by - 0.2)
      ctx.closePath()
      ctx.fill()
    }
  }
  // 枝の割れ (本筋から斜めに短く)。本筋が通り過ぎた所だけ。
  for (let b = 0; b < 4; b += 1) {
    const s0 = total * (0.18 + hash01(g.seed + b * 7.3) * 0.62)
    if (s0 > reach) continue
    const grow = clamp01((reach - s0) / 18)
    const len = (10 + hash01(g.seed + b * 5.9) * 16) * grow
    const up = hash01(g.seed + b * 2.2) > 0.5 ? -1 : 1
    const x0 = g.buttX + g.dir * s0, y0 = crackY(g, s0)
    const x1 = x0 + g.dir * len * 0.75, y1 = y0 + up * len * 0.35
    const hw = 1.0 + hash01(g.seed + b * 8.1) * 1.4
    ctx.globalAlpha = 0.85 * fade
    ctx.fillStyle = '#060509'
    ctx.beginPath()
    ctx.moveTo(x0, y0 - hw)
    ctx.lineTo((x0 + x1) / 2 + 0.8, (y0 + y1) / 2 - hw * 0.8)
    ctx.lineTo(x1, y1)
    ctx.lineTo((x0 + x1) / 2 - 0.6, (y0 + y1) / 2 + hw * 0.7)
    ctx.lineTo(x0, y0 + hw)
    ctx.closePath()
    ctx.fill()
  }
  ctx.restore()
  // 奥の鈍い赤 (割れ目の底の熱だけ・粒で)
  const glowN = Math.max(1, Math.floor(reach / 9))
  for (let i = 0; i < glowN; i += 1) {
    if (hash01(g.seed + i * 2.47) < 0.55) continue
    const s = (i + hash01(g.seed + i * 6.7)) * (reach / glowN)
    const x = g.buttX + g.dir * s
    const y = crackY(g, s) + 0.5
    const r = 1.8 + hash01(g.seed + i * 3.3) * 2.6 + clamp01(s / total) * 2
    glowMote(ctx, x, y, r, 'rgb(150,24,22)', (0.14 + hash01(g.seed + i * 9.1) * 0.16) * fade)
  }
  // 走っている先端の一瞬の熱
  if (front < 1) glowMote(ctx, g.buttX + g.dir * reach, crackY(g, reach), 6, 'rgb(200,60,36)', 0.4 * fade)
}

// 帯の中の石の板。帯を不揃いな幅で区切る (等間隔にしない)。
type Slab = { x: number; w: number; th: number; peak: number; tilt: number; seed: number; delay: number }
function slabsOf(g: ButtGeom): Slab[] {
  const raw: number[] = []
  let sum = 0
  for (let i = 0; i < 5; i += 1) { const v = 0.5 + hash01(g.seed + i * 2.71) * 1.3; raw.push(v); sum += v }
  const span = g.w + 12
  const out: Slab[] = []
  let acc = g.cx - span / 2
  for (let i = 0; i < raw.length; i += 1) {
    const w = (raw[i]! / sum) * span
    const s = g.seed + i * 13.7
    // 石突に近い側から順に、ごくわずかに遅れて持ち上がる (割れ目が抜けていく向き)
    const order = g.dir > 0 ? i : raw.length - 1 - i
    out.push({
      x: acc + w / 2, w: w * (0.88 + hash01(s) * 0.1), th: 8 + hash01(s + 1) * 7,
      peak: 16 + hash01(s + 2) * 30, tilt: (hash01(s + 3) > 0.5 ? 1 : -1) * (0.35 + hash01(s + 7) * 0.8) + g.dir * 0.15,
      seed: s, delay: order * 5,
    })
    acc += w
  }
  return out
}

function drawSlab(ctx: CanvasRenderingContext2D, sl: Slab, ms: number, fade: number): void {
  const t = ms - sl.delay
  if (t < 0) return
  // 0〜70ms で跳ね上がり、その後は重さで落ちて砕けながら沈む
  let lift: number, ang: number
  if (t < 70) { const p = easeOut(t / 70); lift = sl.peak * p; ang = sl.tilt * p }
  else {
    const tf = t - 70
    lift = Math.max(-2, sl.peak - 0.0011 * tf * tf)
    ang = sl.tilt * (1 + tf / 260)
  }
  const half = sl.w / 2
  // 割れて二つに分かれる (90ms 以降、少しずつ離れる)
  const split = t > 90 ? Math.min(6, (t - 90) * 0.035) : 0
  const pieces = split > 0 ? [[-half, -0.1 - split * 0.5, -split * 0.03], [0.1, half, split * 0.04]] : [[-half, half, 0]]
  for (const [l, r, extra] of pieces as Array<[number, number, number]>) {
    ctx.save()
    ctx.globalAlpha = fade
    const off = split > 0 ? (l < 0 ? -split : split) : 0
    ctx.translate(sl.x + off, FLOOR + 2 - lift)
    ctx.rotate(ang + extra)
    // 側面 (暗い石の厚み) — 縁をぎざぎざに欠けさせる
    const jag = (k: number) => (hash01(sl.seed + k * 1.3) - 0.5) * 2.4
    ctx.beginPath()
    ctx.moveTo(l, -sl.th * 0.5 + jag(1))
    ctx.lineTo((l + r) / 2, -sl.th * 0.55 + jag(2))
    ctx.lineTo(r, -sl.th * 0.5 + jag(3))
    ctx.lineTo(r + jag(4) * 0.6, sl.th * 0.5)
    ctx.lineTo((l + r) / 2 + jag(5), sl.th * 0.5 + Math.abs(jag(6)))
    ctx.lineTo(l, sl.th * 0.5 + jag(7) * 0.5)
    ctx.closePath()
    ctx.fillStyle = '#3d3a49'
    ctx.fill()
    // 上の面 (床の石畳の表・明るめの灰)
    ctx.beginPath()
    ctx.moveTo(l, -sl.th * 0.5 + jag(1))
    ctx.lineTo((l + r) / 2, -sl.th * 0.55 + jag(2))
    ctx.lineTo(r, -sl.th * 0.5 + jag(3))
    ctx.lineTo(r - 1, -sl.th * 0.5 + jag(3) + 3.2)
    ctx.lineTo(l + 1, -sl.th * 0.5 + jag(1) + 3.6)
    ctx.closePath()
    ctx.fillStyle = '#7b788a'
    ctx.fill()
    // 割れ口 (下の面は暗く・赤い熱がかすかに当たる)
    ctx.globalAlpha = fade * 0.8
    ctx.fillStyle = '#1a1720'
    ctx.beginPath()
    ctx.moveTo(l, sl.th * 0.5 + jag(7) * 0.5)
    ctx.lineTo((l + r) / 2 + jag(5), sl.th * 0.5 + Math.abs(jag(6)))
    ctx.lineTo(r + jag(4) * 0.6, sl.th * 0.5)
    ctx.lineTo(r - 2, sl.th * 0.5 - 3)
    ctx.lineTo(l + 2, sl.th * 0.5 - 2.5)
    ctx.closePath()
    ctx.fill()
    ctx.restore()
  }
}

/** 予兆 (溜め 700ms)。帯の床から赤黒い煙がにじみ、終盤は石畳がわずかに浮いて小石が震える。まだ割れない (石突は床を打っていない)。 */
export function drawButtCrackTelegraph(ctx: CanvasRenderingContext2D, hz: ActiveHit, timeSec: number, buttX: number): void {
  const g = buttGeom(hz, buttX)
  const p = clamp01(hz.elapsedMs / Math.max(1, hz.telegraphMs))
  const late = p >= 0.7
  const lateP = late ? (p - 0.7) / 0.3 : 0
  ctx.save()
  const banded = buttFx === 'crack3' || buttFx === 'rift'
  if (banded) {
    // 3版・走るひび: 予兆も帯の横範囲 ±V3_BLEED の中だけ (帯の手前の安全な所へ煙をにじませない)
    ctx.beginPath()
    ctx.rect(g.xStart - V3_BLEED, FLOOR - 120, g.w + V3_BLEED * 2, 140)
    ctx.clip()
  }
  // 帯の床が暗く沈む (床に寝た面)
  softBlob(ctx, g.cx, FLOOR + 3, g.w * (banded ? 0.5 : 0.62), 7, 0, 'rgba(26,4,9,0.9)', 0.22 + 0.38 * p)
  // 目地の奥の鈍い赤 (粒・不揃い)
  for (let i = 0; i < 9; i += 1) {
    const x = g.xStart + 6 + hash01(g.seed + i * 5.1) * (g.w - 12)
    const flick = 0.6 + 0.4 * Math.sin(timeSec * (late ? 19 : 8) + i * 2.1)
    glowMote(ctx, x, FLOOR + 2 + hash01(g.seed + i * 1.9) * 4, 3 + hash01(g.seed + i * 3.7) * 5,
      'rgb(150,18,28)', (0.08 + 0.32 * p) * flick)
  }
  // 赤黒い煙 (弾けない攻撃の予兆の共通の約束)。床から低くにじみ、帯の幅に収める。
  for (let i = 0; i < 16; i += 1) {
    const sp = 0.55 + hash01(g.seed + i * 2.9) * 0.6
    const ph = (timeSec * sp + hash01(g.seed + i * 7.7)) % 1
    const x = g.xStart + 4 + hash01(g.seed + i * 4.3) * (g.w - 8) + (ph - 0.5) * 6 * (hash01(g.seed + i) - 0.5)
    const y = FLOOR + 2 - ph * (10 + 26 * p) * (0.6 + hash01(g.seed + i * 6.1) * 0.6)
    const rx = (5 + ph * 9) * (0.7 + hash01(g.seed + i * 8.3) * 0.7)
    const ry = rx * (0.45 + hash01(g.seed + i * 9.9) * 0.35)
    const env = Math.sin(Math.PI * ph)
    softBlob(ctx, x, y, rx, ry, (hash01(g.seed + i * 3.3) - 0.5) * 0.8,
      i % 3 === 0 ? 'rgba(92,12,22,0.95)' : 'rgba(34,5,11,0.95)', (0.22 + 0.55 * p) * env)
  }
  // 終盤: 石畳がわずかに浮く (板の縁が1〜2px 持ち上がり、下に影) + 小石が震える
  if (late) {
    // 石畳の継ぎ目が持ち上がった影 (不揃いな幅の暗い欠け・帯の中に2〜3か所だけ)
    for (const sl of slabsOf(g)) {
      if (hash01(sl.seed + 21) < 0.4) continue
      const lift = (0.8 + 1.8 * lateP) * (0.6 + hash01(sl.seed + 4) * 0.8) + Math.sin(timeSec * 47 + sl.seed) * 0.6 * lateP
      const hw = sl.w * (0.25 + hash01(sl.seed + 22) * 0.2)
      ctx.globalAlpha = 0.25 + 0.45 * lateP
      ctx.fillStyle = '#08060b'
      ctx.beginPath()
      ctx.moveTo(sl.x - hw, FLOOR + 1.5)
      ctx.lineTo(sl.x - hw * 0.2, FLOOR + 0.5 - lift)
      ctx.lineTo(sl.x + hw * 0.7, FLOOR + 1 - lift * 0.6)
      ctx.lineTo(sl.x + hw, FLOOR + 2)
      ctx.lineTo(sl.x, FLOOR + 3 + lift * 0.4)
      ctx.closePath()
      ctx.fill()
    }
    for (let i = 0; i < 7; i += 1) {
      const x = g.xStart + 4 + hash01(g.seed + i * 11.3) * (g.w - 8)
      const hop = Math.abs(Math.sin(timeSec * (28 + i * 3) + i)) * (1 + 3 * lateP)
      stoneChip(ctx, x, FLOOR - hop, 1.2 + hash01(g.seed + i * 2.6) * 1.6, i, g.seed + i * 3.9, 0.85)
    }
  }
  ctx.restore()
}

/** 当たり (260ms)。既定 `rift` = 走るひび (10-20)・`crack3` = 3版・`crack` = 2版 (強い噴き上がり)・`crack1` = 1版 (比較用に残す)。 */
export function drawButtCrackActive(ctx: CanvasRenderingContext2D, hz: ActiveHit, timeSec: number, buttX: number): void {
  if (buttFx === 'crack1') { drawButtCrackActiveV1(ctx, hz, timeSec, buttX); return }
  const g = buttGeom(hz, buttX)
  const ms = Math.max(0, hz.elapsedMs - hz.telegraphMs)
  const look: TraceLook = buttFx === 'rift' ? 'rift' : buttFx === 'crack3' ? 'v3' : 'v2'
  v2Traces.set(hz.id, { g, startSec: timeSec - ms / 1000, activeMs: hz.activeMs, look })
  drawEruptionOf(ctx, look, g, ms, hz.activeMs)
}

/** 1版: 石突の着点から割れ目が走り (約2コマ)、帯で石の板が持ち上がって砕け、灰の土煙が立つ。 */
function drawButtCrackActiveV1(ctx: CanvasRenderingContext2D, hz: ActiveHit, _timeSec: number, buttX: number): void {
  const g = buttGeom(hz, buttX)
  const ms = Math.max(0, hz.elapsedMs - hz.telegraphMs)
  const life = Math.max(1, hz.activeMs)
  const fade = ms < life * 0.72 ? 1 : clamp01(1 - (ms - life * 0.72) / (life * 0.28))
  const front = easeOut((ms + RUN_LEAD_MS) / RUN_MS)
  // 帯の床が抜けた穴: 板が持ち上がった所だけ、不揃いな黒い穴が開く (帯全体の四角い帯にしない)
  const open = easeOut(ms / 50)
  ctx.save()
  for (const sl of slabsOf(g)) {
    const hw = sl.w * (0.42 + hash01(sl.seed + 9) * 0.12)
    const d = 4 + hash01(sl.seed + 10) * 5
    ctx.globalAlpha = 0.9 * fade * open
    ctx.fillStyle = '#060508'
    ctx.beginPath()
    ctx.moveTo(sl.x - hw, FLOOR + 1 + hash01(sl.seed + 11) * 2)
    ctx.lineTo(sl.x - hw * 0.3, FLOOR - 0.5 - hash01(sl.seed + 12) * 1.5)
    ctx.lineTo(sl.x + hw * 0.4, FLOOR + 0.2 - hash01(sl.seed + 13) * 1.5)
    ctx.lineTo(sl.x + hw, FLOOR + 1.5 + hash01(sl.seed + 14) * 2)
    ctx.lineTo(sl.x + hw * 0.6, FLOOR + d)
    ctx.lineTo(sl.x - hw * 0.5, FLOOR + d * (0.7 + hash01(sl.seed + 15) * 0.5))
    ctx.closePath()
    ctx.fill()
    glowMote(ctx, sl.x + (hash01(sl.seed + 16) - 0.5) * hw, FLOOR + d * 0.5, 4 + hash01(sl.seed + 17) * 5,
      'rgb(160,24,22)', (0.18 + 0.2 * (1 - clamp01(ms / 140))) * fade * open)
  }
  ctx.restore()

  // 走る割れ目
  drawRunningCrack(ctx, g, front, fade)

  // 石突の着点の小さな土煙
  const bp = clamp01(ms / 160)
  for (let i = 0; i < 4; i += 1) {
    const rx = (5 + bp * 12) * (0.7 + hash01(g.seed + i * 3.4) * 0.6)
    softBlob(ctx, g.buttX + g.dir * (hash01(g.seed + i) * 10 - 2), FLOOR - 2 - bp * (4 + i * 3), rx, rx * 0.55,
      (hash01(g.seed + i * 5.5) - 0.5), 'rgba(116,110,120,0.9)', 0.38 * (1 - bp) * fade)
  }

  // 土煙 (後ろ側): 帯から灰がもくもくと立つ。床の近くほど濃い。
  ctx.save()
  for (let i = 0; i < 18; i += 1) {
    const s = g.seed + i * 9.17
    const delay = hash01(s) * 30
    const t = ms - delay
    if (t < 0) continue
    const p = clamp01(t / (life - delay))
    const grow = easeOut(t / 120)
    const x = g.xStart + 2 + hash01(s + 1) * (g.w - 4) + g.dir * grow * (hash01(s + 2) - 0.3) * 18
    const high = hash01(s + 3)
    const y = FLOOR - 4 - grow * (8 + high * 58)
    const rx = (7 + grow * 16) * (0.7 + hash01(s + 4) * 0.6)
    const ry = rx * (0.5 + hash01(s + 5) * 0.35)
    const a = (0.72 - high * 0.3) * (1 - p * p) * fade
    softBlob(ctx, x, y, rx, ry, (hash01(s + 6) - 0.5) * 0.9, high > 0.6 ? 'rgba(116,111,124,0.92)' : 'rgba(150,144,154,0.95)', a)
  }
  ctx.restore()

  // 石の板 (持ち上がって割れる)
  for (const sl of slabsOf(g)) drawSlab(ctx, sl, ms, fade)

  // 飛ぶ石くず (大きさ・速さ・向きを不揃いに)
  for (let i = 0; i < 26; i += 1) {
    const s = g.seed + i * 5.31
    const delay = hash01(s) * 25
    const t = ms - delay
    if (t < 0) continue
    const vx = (hash01(s + 1) - 0.42) * 0.22 + g.dir * 0.04
    const vy = 0.18 + hash01(s + 2) * 0.32
    const x0 = g.xStart + hash01(s + 3) * g.w
    const x = x0 + vx * t
    const y = FLOOR - 2 - (vy * t - 0.0016 * t * t)
    if (y > FLOOR + 4) continue
    const r = 1.1 + hash01(s + 4) * hash01(s + 5) * 4.4
    stoneChip(ctx, x, y, r, t * 0.02 * (hash01(s + 6) - 0.5) * 3, s, 0.95 * fade)
  }
  // 割れ目が通り過ぎた所から跳ねる小石
  const total = Math.abs(g.inner - g.buttX)
  for (let i = 0; i < 8; i += 1) {
    const s = g.seed + i * 7.77
    const at = total * (0.1 + hash01(s) * 0.85)
    const passMs = Math.max(0, (Math.sqrt(Math.max(0, at / (total + g.w * 0.25))) * RUN_MS) - RUN_LEAD_MS)
    const t = ms - passMs
    if (t < 0) continue
    const vy = 0.10 + hash01(s + 1) * 0.16
    const y = FLOOR + 1 - (vy * t - 0.0014 * t * t)
    if (y > FLOOR + 3) continue
    stoneChip(ctx, g.buttX + g.dir * (at + t * 0.03), y, 0.9 + hash01(s + 2) * 1.6, t * 0.03, s, 0.9 * fade)
  }

  // 土煙 (手前側の薄い層): 板と石くずに少しかぶせて、帯の中で噴いたように見せる。
  for (let i = 0; i < 9; i += 1) {
    const s = g.seed + i * 12.9 + 300
    const grow = easeOut((ms - hash01(s) * 20) / 140)
    if (grow <= 0) continue
    const x = g.xStart + hash01(s + 1) * g.w
    const y = FLOOR - 2 - grow * (4 + hash01(s + 2) * 20)
    const rx = (6 + grow * 13) * (0.7 + hash01(s + 3) * 0.6)
    softBlob(ctx, x, y, rx, rx * (0.45 + hash01(s + 4) * 0.3), (hash01(s + 5) - 0.5) * 0.7,
      'rgba(156,150,158,0.9)', 0.40 * (1 - clamp01(ms / life)) * fade)
  }
}

// ---------------------------------------------------------------- K-2 接点の星＋火花

export const LANCE_STAR_LIFE_SEC = 0.34
// 接点 = 主人公の胸の前 (剣を構える高さ)。hits.ts の lance_flash は (p.x, p.y−60) で出る。
//   ここで体の前 (騎士の側) へ寄せ、高さを胸へ合わせる (顔・頭に刺さって見せない)。
export const LANCE_STAR_FRONT_PX = 12
export const LANCE_STAR_DROP_PX = -2

/** 突きが主人公に当たった瞬間。暖かい白の小さな星形のにじみ + 突きの向きへだけ散る火の粉。金と青白は使わない。 */
export function drawLanceStar(ctx: CanvasRenderingContext2D, fx: number, fy: number, age: number, dir: number): void {
  if (age < 0 || age > LANCE_STAR_LIFE_SEC) return
  const x = fx - dir * LANCE_STAR_FRONT_PX
  const y = fy + LANCE_STAR_DROP_PX
  const seed = Math.round(fx * 7.3 + fy * 1.1)
  const ms = age * 1000
  const thrust = dir < 0 ? Math.PI : 0

  // 星 (0〜170ms): 不揃いな花弁を柔らかくぼかして重ねる
  const st = clamp01(ms / 170)
  const sa = (1 - st) * (1 - st)
  if (sa > 0.01) {
    glowMote(ctx, x, y, 26 + 10 * st, 'rgb(255,86,46)', 0.30 * sa)
    glowMote(ctx, x, y, 11 + 4 * st, 'rgb(255,206,166)', 0.55 * sa)
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    ctx.filter = `blur(${blurPx(1.1)}px)`
    // 花弁の向き: 突きの向き (前へ抜ける) を一番長く、残りは不揃いな角度
    const petals: Array<[number, number, number]> = [[thrust + (hash01(seed) - 0.5) * 0.2, 25, 5.5]]
    // 残りの花弁は角度・長さを大きくばらす (整った星の記号に見せない)。後ろ (騎士の側) へは短く。
    for (let i = 0; i < 4; i += 1) {
      const ang = thrust + Math.PI * (0.38 + i * 0.41) + (hash01(seed + i * 3.1) - 0.5) * 0.65
      const back = Math.max(0, -Math.cos(ang - thrust)) // 1 = 真後ろ
      petals.push([ang, (11 + hash01(seed + i * 5.3) * 11) * (1 - back * 0.3), 3.4 + hash01(seed + i * 7.9) * 2.4])
    }
    for (const [ang, len0, wid] of petals) {
      const len = len0 * (1 - st * 0.55)
      const cx = Math.cos(ang), cy = Math.sin(ang)
      const px = -cy, py = cx
      for (const [k, a, lm] of [[1.5, 0.28, 0.8], [1, 0.95, 1]] as Array<[number, number, number]>) {
        const L = len * lm, W = wid * k
        const gr = ctx.createRadialGradient(x, y, 0, x, y, L)
        gr.addColorStop(0, 'rgba(255,250,240,1)')
        gr.addColorStop(0.35, 'rgba(255,224,190,0.85)')
        gr.addColorStop(0.75, 'rgba(255,128,80,0.45)')
        gr.addColorStop(1, 'rgba(255,90,50,0)')
        ctx.globalAlpha = a * sa
        ctx.fillStyle = gr
        ctx.beginPath()
        ctx.moveTo(x - cx * W * 0.4, y - cy * W * 0.4)
        ctx.lineTo(x + cx * L * 0.18 + px * W * 0.5, y + cy * L * 0.18 + py * W * 0.5)
        ctx.lineTo(x + cx * L, y + cy * L)
        ctx.lineTo(x + cx * L * 0.18 - px * W * 0.5, y + cy * L * 0.18 - py * W * 0.5)
        ctx.closePath()
        ctx.fill()
      }
    }
    ctx.restore()
    glowMote(ctx, x, y, 6 * (1 - st * 0.4), 'rgb(255,247,232)', 0.95 * sa)
  }

  // 火の粉 (突きの向きへだけ・扇 ±30°)。頭の粒 + 遅れて薄くなる粒2つ (線にしない)。
  for (let i = 0; i < 15; i += 1) {
    const s = seed + i * 4.17
    const delay = hash01(s) * 22
    const lifeMs = 140 + hash01(s + 1) * 170
    const t = ms - delay
    if (t < 0 || t > lifeMs) continue
    const ang = thrust + (hash01(s + 2) - 0.55) * 1.05 * (dir < 0 ? -1 : 1)
    const sp = 0.30 + hash01(s + 3) * 0.55
    const u = t / lifeMs
    const r = (0.9 + hash01(s + 4) * 1.8) * (1 - u * 0.5)
    const col = u < 0.3 ? 'rgb(255,240,214)' : u < 0.6 ? 'rgb(255,160,88)' : 'rgb(236,78,44)'
    for (let k = 0; k < 3; k += 1) {
      const tk = Math.max(0, t - k * 9)
      const sx = x + Math.cos(ang) * sp * tk
      const sy = y + Math.sin(ang) * sp * tk + 0.0011 * tk * tk
      glowMote(ctx, sx, sy, r * (k === 0 ? 2.2 : 1.6 - k * 0.3), col, (1 - u) * (k === 0 ? 0.95 : 0.4 - k * 0.1))
    }
  }

  // 灰の小さな粒 (接点から下へ落ちる)
  for (let i = 0; i < 6; i += 1) {
    const s = seed + i * 9.3 + 50
    const t = ms - hash01(s) * 30
    if (t < 0) continue
    const u = clamp01(t / 300)
    const gx = x + Math.cos(thrust) * (4 + hash01(s + 1) * 14) * u * 2 + (hash01(s + 2) - 0.5) * 8
    const gy = y + 3 + 0.0009 * t * t + hash01(s + 3) * 6
    stoneChip(ctx, gx, gy, 0.8 + hash01(s + 4) * 1.1, t * 0.02, s, 0.7 * (1 - u), '#4d4954', '#8d8896')
  }
}

// ---------------------------------------------------------------- K-1 2版 (10-08 窓口「見本 k1_A_crack に比べて弱すぎる」)
//   ① 割れ目の奥に赤熱した芯 (暗い朱〜赤の面と粒)・縁の欠けを明るく・幅を太く
//   ② 帯の中で大小ばらばらの石の破片 (数十) と灰の土煙が主人公の腰の高さまで噴く。土煙は当たりの後も少し残る
//   ③ 当たりの最初の数コマは帯の床と破片の下側に赤い照り返し (弾けない攻撃の赤)
//   当たり・時間は変えない (帯の全体が 0ms から当たるのはそのまま)。溜めは1版と同じ。

const GRAV = 0.0026            // px/ms² (破片の重さ)
const V2_LINGER_MS = 520       // 当たり (260ms) の後も土煙・転がった石を残す長さ
type TraceLook = 'v2' | 'v3' | 'rift'
type V2Trace = { g: ButtGeom; startSec: number; activeMs: number; look: TraceLook }
const v2Traces = new Map<number, V2Trace>()

/** 当たりが消えた後の残り (土煙が晴れていく)。render.ts の drawHazards の最後で呼ぶ。描くだけ。 */
export function drawButtCrackLinger(ctx: CanvasRenderingContext2D, timeSec: number, hits: readonly ActiveHit[]): void {
  if (v2Traces.size === 0) return
  for (const [id, tr] of v2Traces) {
    const ms = (timeSec - tr.startSec) * 1000
    const linger = tr.look === 'rift' ? RIFT_LINGER_MS : tr.look === 'v3' ? V3_LINGER_MS : V2_LINGER_MS
    if (ms < 0 || ms > tr.activeMs + linger) { v2Traces.delete(id); continue }
    if (hits.some((h) => h.id === id)) continue   // まだ当たりの中 = 本体側で描いている
    drawEruptionOf(ctx, tr.look, tr.g, ms, tr.activeMs)
  }
}

function drawEruptionOf(ctx: CanvasRenderingContext2D, look: TraceLook, g: ButtGeom, ms: number, activeMs: number): void {
  if (look === 'rift') drawEruptionRift(ctx, g, ms, activeMs)
  else if (look === 'v3') drawEruptionV3(ctx, g, ms, activeMs)
  else drawEruptionV2(ctx, g, ms, activeMs)
}

function smooth01(a: number, b: number, v: number): number {
  const p = clamp01((v - a) / (b - a))
  return p * p * (3 - 2 * p)
}

// 赤い照り返しの強さ: 最初の数コマは今の版と同じくらい、その後すっと引く
function redFlash(ms: number): number { return ms < 50 ? 1 : clamp01(1 - (ms - 50) / 150) }

function lighterBlob(ctx: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number, color: string, a: number): void {
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  softBlob(ctx, x, y, rx, ry, 0, color, a)
  ctx.restore()
}

type Pt = [number, number]
function polyPath(ctx: CanvasRenderingContext2D, pts: Pt[], back: Pt[], dy = 0, dyb = 0): void {
  ctx.beginPath()
  pts.forEach(([x, y], i) => { if (i === 0) ctx.moveTo(x, y + dy); else ctx.lineTo(x, y + dy) })
  for (let i = back.length - 1; i >= 0; i -= 1) ctx.lineTo(back[i]![0], back[i]![1] + dyb)
  ctx.closePath()
}

// 割れ目 (2版): 太い黒い裂け目 + 奥の赤熱した芯 + 明るい欠けの縁
function drawCrackV2(ctx: CanvasRenderingContext2D, g: ButtGeom, ms: number): void {
  const front = easeOut((ms + RUN_LEAD_MS) / RUN_MS)
  const fade = 1 - smooth01(320, 640, ms)
  const heat = 1 - smooth01(60, 520, ms)
  const total = Math.abs(g.inner - g.buttX) + g.w * 0.3
  const reach = total * front
  if (reach < 2 || fade <= 0) return
  const step = 4
  const n = Math.max(2, Math.ceil(reach / step))
  const top: Pt[] = []
  const bot: Pt[] = []
  const coreT: Pt[] = []
  const coreB: Pt[] = []
  for (let i = 0; i <= n; i += 1) {
    const s = Math.min(reach, i * step)
    const x = g.buttX + g.dir * s
    const y = crackY(g, s)
    const u = clamp01(s / total)
    const chip = hash01(g.seed + Math.round(s / 5) * 1.91)
    const h = (i === 0 || i === n) ? 0.8 : 2.2 + u * 4.4 + (chip > 0.62 ? chip * 4 : chip * 1.2)
    top.push([x + (hash01(g.seed + i * 0.61) - 0.5) * 2.4, y - h])
    bot.push([x + (hash01(g.seed + i * 0.83) - 0.5) * 2.4, y + h * 0.75])
    const ch = h * (0.35 + hash01(g.seed + i * 1.37) * 0.25)
    coreT.push([x, y - ch * 0.55])
    coreB.push([x, y + ch * 0.5])
  }
  ctx.save()
  // 沈んだ床の影 (割れ目のまわりが少し落ちくぼむ)
  ctx.globalAlpha = 0.55 * fade
  ctx.fillStyle = '#0a080d'
  polyPath(ctx, top, bot, -3, 4)
  ctx.fill()
  // 裂け目 = 欠けた石の黒い面
  ctx.globalAlpha = 0.97 * fade
  ctx.fillStyle = '#040306'
  polyPath(ctx, top, bot)
  ctx.fill()
  // 奥の赤熱した芯 (暗い朱の面・幅が場所ごとに違う)
  if (heat > 0.02) {
    ctx.globalAlpha = 0.95 * fade * (0.35 + 0.65 * heat)
    ctx.fillStyle = 'rgb(118,20,14)'
    polyPath(ctx, coreT, coreB)
    ctx.fill()
  }
  // 縁の欠け: 上は光を受けて明るく、下は影
  for (let i = 1; i < top.length - 1; i += 1) {
    const hv = hash01(g.seed + i * 4.07)
    if (hv < 0.56) continue
    const [x0, y] = top[i]!
    const x = x0 + (hash01(g.seed + i * 5.13) - 0.5) * step * 1.5
    const wv = 3 + hash01(g.seed + i * 6.29) * 12
    const lean = (hash01(g.seed + i * 7.77) - 0.5) * wv * 0.6
    const rise = 0.8 + hash01(g.seed + i * 8.31) * 2.2
    ctx.globalAlpha = (0.45 + hv * 0.4) * fade
    ctx.fillStyle = hv > 0.86 ? '#c4c0d2' : '#9893a8'
    ctx.beginPath()
    // 欠けた石の上の面 (台形・高さと傾きがばらばら)
    ctx.moveTo(x - wv * 0.55, y + 0.5)
    ctx.lineTo(x - wv * 0.3 + lean, y - rise)
    ctx.lineTo(x + wv * 0.25 + lean, y - rise * (0.5 + hash01(g.seed + i * 9.1) * 0.6))
    ctx.lineTo(x + wv * 0.5, y + 0.3)
    ctx.closePath()
    ctx.fill()
    if (hv > 0.66) {
      const [bx, by] = bot[i]!
      ctx.globalAlpha = 0.75 * fade
      ctx.fillStyle = '#141219'
      ctx.beginPath()
      ctx.moveTo(bx - wv * 0.5, by - 0.3)
      ctx.lineTo(bx + wv * 0.15, by + 2 + hv * 2)
      ctx.lineTo(bx + wv * 0.6, by - 0.2)
      ctx.closePath()
      ctx.fill()
    }
  }
  // 枝の割れ (本筋から斜めに・それぞれ奥に赤)
  for (let b = 0; b < 5; b += 1) {
    const s0 = total * (0.12 + hash01(g.seed + b * 7.3) * 0.72)
    if (s0 > reach) continue
    const grow = clamp01((reach - s0) / 14)
    const len = (12 + hash01(g.seed + b * 5.9) * 20) * grow
    const up = hash01(g.seed + b * 2.2) > 0.5 ? -1 : 1
    const x0 = g.buttX + g.dir * s0, y0 = crackY(g, s0)
    const x1 = x0 + g.dir * len * 0.72, y1 = y0 + up * len * 0.38
    const hw = 1.4 + hash01(g.seed + b * 8.1) * 1.8
    const mx = (x0 + x1) / 2, my = (y0 + y1) / 2
    ctx.globalAlpha = 0.92 * fade
    ctx.fillStyle = '#050407'
    ctx.beginPath()
    ctx.moveTo(x0, y0 - hw)
    ctx.lineTo(mx + 1, my - hw * 0.9)
    ctx.lineTo(x1, y1)
    ctx.lineTo(mx - 0.8, my + hw * 0.8)
    ctx.lineTo(x0, y0 + hw)
    ctx.closePath()
    ctx.fill()
    if (heat > 0.05) glowMote(ctx, mx, my, 3 + hw, 'rgb(200,48,28)', 0.45 * heat * fade)
  }
  ctx.restore()
  // 芯の熱 (粒): 大きさ・間隔をばらして、裂け目に沿った赤熱の溜まりにする
  if (heat > 0.02) {
    let s = 2 + hash01(g.seed) * 3
    let k = 0
    while (s < reach) {
      const x = g.buttX + g.dir * s
      const y = crackY(g, s) + 0.3
      const u = clamp01(s / total)
      const hv = hash01(g.seed + k * 2.93)
      const flick = 0.8 + 0.2 * Math.sin(ms * 0.05 + k * 1.7)
      glowMote(ctx, x, y, (2 + hv * 3.5) * (0.7 + u * 0.8), hv > 0.55 ? 'rgb(255,120,56)' : 'rgb(214,52,30)',
        (0.45 + hv * 0.4) * heat * fade * flick)
      if (hv > 0.7) glowMote(ctx, x, y, 9 + hv * 7, 'rgb(170,26,22)', 0.28 * heat * fade)
      s += 3 + hash01(g.seed + k * 5.71) * 6
      k += 1
    }
    if (front < 1) {
      const fx = g.buttX + g.dir * reach, fy = crackY(g, reach)
      glowMote(ctx, fx, fy, 12, 'rgb(230,70,36)', 0.7 * fade)
      glowMote(ctx, fx, fy, 4, 'rgb(255,200,150)', 0.8 * fade)
    }
  }
}

// 割れた石畳の塊 (2版)。形・陰影・大きさを不揃いに。
type Chunk = { x: number; w: number; th: number; peak: number; spin: number; vx: number; seed: number; delay: number; pts: Pt[] }
function chunksOf(g: ButtGeom): Chunk[] {
  const raw: number[] = []
  let sum = 0
  for (let i = 0; i < 5; i += 1) { const v = 0.45 + hash01(g.seed + i * 2.71 + 40) * 1.4; raw.push(v); sum += v }
  const span = g.w + 24
  const out: Chunk[] = []
  let acc = g.cx - span / 2
  for (let i = 0; i < raw.length; i += 1) {
    const w = (raw[i]! / sum) * span
    const s = g.seed + i * 17.3 + 40
    const th = 13 + hash01(s + 1) * 14
    const pts: Pt[] = []
    const nv = 6 + Math.floor(hash01(s + 8) * 3)
    for (let k = 0; k < nv; k += 1) {
      const a = (k / nv) * Math.PI * 2 + (hash01(s + k * 1.9) - 0.5) * 0.7
      const rx = w * 0.5 * (0.7 + hash01(s + k * 2.7) * 0.4)
      const ry = th * 0.5 * (0.65 + hash01(s + k * 3.3) * 0.55)
      pts.push([Math.cos(a) * rx, Math.sin(a) * ry])
    }
    const order = g.dir > 0 ? i : raw.length - 1 - i
    out.push({
      x: acc + w / 2, w: w * 0.92, th, peak: 22 + hash01(s + 2) * 38,
      spin: (hash01(s + 3) > 0.5 ? 1 : -1) * (0.0025 + hash01(s + 7) * 0.006),
      vx: (hash01(s + 9) - 0.5) * 0.09 + g.dir * 0.02, seed: s, delay: order * 6, pts,
    })
    acc += w
  }
  return out
}

function drawChunk(ctx: CanvasRenderingContext2D, c: Chunk, ms: number, red: number, fade: number): void {
  const t = ms - c.delay
  if (t < 0 || fade <= 0) return
  const vy = Math.sqrt(2 * GRAV * c.peak)
  const flight = (2 * vy) / GRAV
  const tt = Math.min(t, flight)
  const lift = Math.max(0, vy * tt - 0.5 * GRAV * tt * tt)
  const ang = c.spin * tt
  // 120ms 以降、大きい塊は二つに割れて離れる
  const split = c.w > 18 && t > 120 ? Math.min(7, (t - 120) * 0.04) : 0
  const halves: Array<[number, number]> = split > 0 ? [[-1, -split], [1, split]] : [[0, 0]]
  for (const [side, off] of halves) {
    ctx.save()
    ctx.globalAlpha = fade
    ctx.translate(c.x + c.vx * tt + off, FLOOR + 1 - lift)
    ctx.rotate(ang + side * split * 0.05)
    ctx.beginPath()
    if (side !== 0) {
      // 片側の頂点 + 割れ口 (中央のぎざぎざ)
      const pts = c.pts.filter(([x]) => x * side >= -1)
      pts.sort((a, b) => Math.atan2(a[1], a[0]) - Math.atan2(b[1], b[0]))
      ctx.moveTo(side * 1.5, -c.th * 0.45)
      for (const [x, y] of pts) ctx.lineTo(x, y)
      ctx.lineTo(side * (1 + hash01(c.seed + 31) * 3), c.th * 0.4)
      ctx.lineTo(side * (hash01(c.seed + 32) * 3), 0)
    } else {
      c.pts.forEach(([x, y], k) => { if (k === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y) })
    }
    ctx.closePath()
    ctx.fillStyle = '#45424f'
    ctx.fill()
    ctx.save()
    ctx.clip()
    // 上の面 (石畳の表・明るい灰) — 上側の帯だけ
    ctx.fillStyle = '#8e8b9d'
    ctx.fillRect(-c.w, -c.th, c.w * 2, c.th * (0.42 + hash01(c.seed + 12) * 0.12))
    // 斑 (石の汚れ・欠け) — 大きさ不揃い
    for (let k = 0; k < 4; k += 1) {
      ctx.globalAlpha = fade * (0.25 + hash01(c.seed + k * 4.1) * 0.3)
      ctx.fillStyle = k % 2 ? '#2c2a33' : '#6b6879'
      const sx = (hash01(c.seed + k * 5.3) - 0.5) * c.w, sy = (hash01(c.seed + k * 6.7) - 0.3) * c.th * 0.6
      const sr = 1.2 + hash01(c.seed + k * 7.1) * 3.2
      ctx.beginPath()
      ctx.moveTo(sx - sr, sy)
      ctx.lineTo(sx, sy - sr * 0.6)
      ctx.lineTo(sx + sr * 0.8, sy + sr * 0.2)
      ctx.lineTo(sx - sr * 0.2, sy + sr * 0.7)
      ctx.closePath()
      ctx.fill()
    }
    // 下側 (割れ口) = 暗い。最初の数コマは赤い照り返し。
    ctx.globalAlpha = fade
    ctx.fillStyle = '#1a1820'
    ctx.fillRect(-c.w, c.th * 0.12, c.w * 2, c.th)
    if (red > 0.01) {
      ctx.globalAlpha = fade * red * 0.85
      ctx.fillStyle = 'rgb(196,38,28)'
      ctx.fillRect(-c.w, c.th * 0.08, c.w * 2, c.th)
    }
    ctx.restore()
    ctx.restore()
  }
  if (red > 0.01 && lift > 2) glowMote(ctx, c.x + c.vx * tt, FLOOR + 1 - lift + c.th * 0.5, c.w * 0.45, 'rgb(220,50,30)', 0.35 * red * fade)
}

function drawEruptionV2(ctx: CanvasRenderingContext2D, g: ButtGeom, ms: number, activeMs: number): void {
  const red = redFlash(ms)
  const endMs = activeMs + V2_LINGER_MS
  const dustFade = 1 - smooth01(activeMs + 80, endMs, ms)
  const stoneFade = 1 - smooth01(activeMs + 160, endMs, ms)
  const open = easeOut(ms / 40)

  // 赤い照り返し (帯の床・最初の数コマ)。弾けない攻撃の赤。
  if (red > 0.01) {
    lighterBlob(ctx, g.cx, FLOOR + 3, g.w * 0.62 + 16, 11, 'rgba(235,44,32,0.95)', 0.62 * red)
    lighterBlob(ctx, g.cx, FLOOR - 16, g.w * 0.55, 26, 'rgba(190,26,24,0.9)', 0.30 * red)
  }
  // 床が抜けた穴 (塊の下)
  ctx.save()
  for (const c of chunksOf(g)) {
    const hw = c.w * (0.45 + hash01(c.seed + 9) * 0.1)
    const d = 5 + hash01(c.seed + 10) * 6
    ctx.globalAlpha = 0.92 * stoneFade * open
    ctx.fillStyle = '#050407'
    ctx.beginPath()
    ctx.moveTo(c.x - hw, FLOOR + 1 + hash01(c.seed + 11) * 2)
    ctx.lineTo(c.x - hw * 0.3, FLOOR - 0.8 - hash01(c.seed + 12) * 1.5)
    ctx.lineTo(c.x + hw * 0.4, FLOOR - hash01(c.seed + 13) * 1.5)
    ctx.lineTo(c.x + hw, FLOOR + 1.5 + hash01(c.seed + 14) * 2)
    ctx.lineTo(c.x + hw * 0.6, FLOOR + d)
    ctx.lineTo(c.x - hw * 0.5, FLOOR + d * (0.7 + hash01(c.seed + 15) * 0.5))
    ctx.closePath()
    ctx.fill()
    glowMote(ctx, c.x + (hash01(c.seed + 16) - 0.5) * hw, FLOOR + d * 0.45, 5 + hash01(c.seed + 17) * 6,
      'rgb(210,46,28)', (0.25 + 0.5 * red) * (1 - smooth01(80, 460, ms)) * stoneFade * open)
  }
  ctx.restore()

  drawCrackV2(ctx, g, ms)

  // 石突の着点の土煙
  const bp = clamp01(ms / 260)
  for (let i = 0; i < 5; i += 1) {
    const rx = (6 + bp * 16) * (0.7 + hash01(g.seed + i * 3.4) * 0.6)
    softBlob(ctx, g.buttX + g.dir * (hash01(g.seed + i) * 14 - 3), FLOOR - 2 - bp * (5 + i * 4), rx, rx * 0.55,
      (hash01(g.seed + i * 5.5) - 0.5), 'rgba(140,134,146,0.92)', 0.5 * (1 - bp) * dustFade)
  }

  // 土煙 (後ろの層): 腰の高さまで大きく噴いて、当たりの後もしばらく漂ってから晴れる
  for (let i = 0; i < 30; i += 1) {
    const s = g.seed + i * 9.17 + 500
    const t = ms - hash01(s) * 50
    if (t < 0) continue
    const grow = easeOut(t / 260)
    const drift = t / 1000
    const high = hash01(s + 3)
    const x = g.xStart - 6 + hash01(s + 1) * (g.w + 12) + (hash01(s + 2) - 0.5) * 30 * grow + g.dir * drift * 10
    const y = FLOOR - 6 - grow * (10 + high * 52) - drift * 4
    const rx = (8 + grow * 20 + drift * 10) * (0.65 + hash01(s + 4) * 0.7)
    const ry = rx * (0.5 + hash01(s + 5) * 0.35)
    const tone = hash01(s + 6)
    const col = tone > 0.75 ? 'rgba(176,170,180,0.95)' : tone > 0.3 ? 'rgba(146,140,152,0.95)' : 'rgba(100,95,108,0.95)'
    softBlob(ctx, x, y, rx, ry, (hash01(s + 7) - 0.5) * 0.9, col, (0.78 - high * 0.3) * dustFade * Math.min(1, t / 30 + 0.3))
  }

  // 割れた石畳の塊
  for (const c of chunksOf(g)) drawChunk(ctx, c, ms, red, stoneFade)

  // 破片 (数十・不揃いな多角形・回転しながら放物線)。落ちたら床に転がって残る。
  for (let i = 0; i < 48; i += 1) {
    const s = g.seed + i * 5.31 + 900
    const t = ms - hash01(s) * 40
    if (t < 0) continue
    const big = hash01(s + 4) * hash01(s + 5)
    const r = 1.2 + big * 8
    const vy = 0.26 + hash01(s + 2) * 0.33 * (1 - big * 0.4)
    const vx = (hash01(s + 1) - 0.5) * 0.24 + g.dir * 0.03
    const flight = (2 * vy) / GRAV
    const tt = Math.min(t, flight)
    const x = g.xStart + hash01(s + 3) * g.w + vx * tt
    const y = FLOOR - 1 - Math.max(0, vy * tt - 0.5 * GRAV * tt * tt) + (t >= flight ? 1.5 : 0)
    const rot = (hash01(s + 6) - 0.5) * 0.03 * tt
    stoneChip(ctx, x, y, r, rot, s, 0.97 * stoneFade, '#34313d', hash01(s + 7) > 0.5 ? '#a19db0' : '#7f7b8e')
    if (red > 0.05 && r > 3 && t < flight) glowMote(ctx, x, y + r * 0.6, r * 1.4, 'rgb(220,52,30)', 0.4 * red * stoneFade)
  }

  // 土煙 (手前の薄い層): 塊と破片に少しかぶせる
  for (let i = 0; i < 12; i += 1) {
    const s = g.seed + i * 12.9 + 300
    const t = ms - hash01(s) * 30
    if (t <= 0) continue
    const grow = easeOut(t / 220)
    const x = g.xStart + hash01(s + 1) * g.w + (hash01(s + 6) - 0.5) * 16 * grow
    const y = FLOOR - 3 - grow * (6 + hash01(s + 2) * 34)
    const rx = (7 + grow * 16) * (0.7 + hash01(s + 3) * 0.6)
    softBlob(ctx, x, y, rx, rx * (0.45 + hash01(s + 4) * 0.3), (hash01(s + 5) - 0.5) * 0.7,
      'rgba(164,158,168,0.9)', 0.42 * dustFade)
  }
}

// ---------------------------------------------------------------- K-1 3版 (10-08 ユーザー「見本絵に近づけて・光の範囲を当たりの位置と一致」)
//   DEV `?knightButtFx=crack3`。当たりの帯 (騎士の前 +120〜+220px) は変えない
//   (docs/research/2026-10-08-knight-butt-trap-align.md: 実戦の場面で「留まる・攻撃・前＝安全、後ろへ回避＝当たる」が既に成り立つ)。
//   ① 危険に見える物 (赤い照り返し・石の板・破片・土煙) は帯の横範囲の中だけ (端のにじみ ≤ 約8px)。帯の外は描画を切り取る。
//   ② 主役 = 床から斜めに突き立つ大きな石畳の板 (見本 k1_A_crack)。破片と土煙は脇役・低め (腰まで)。
//   ③ 石突の着点から帯までは、赤も破片も無い暗い細いひびだけが一瞬走って消える (打った因果は見せ、安全な所を危険に見せない)。

const V3_LINGER_MS = 300
const V3_BLEED = 8             // 帯の外へにじんでよい幅 (px)

type Slab3 = { px: number; len: number; th: number; lean: number; seed: number; delay: number }
function slabs3Of(g: ButtGeom): Slab3[] {
  // 割れて持ち上がった石畳の板。根元の位置・長さ・幅・傾きを1枚ずつ大きくばらす (等間隔・同じ形の尖りの並びにしない)。
  const n = 7
  const out: Slab3[] = []
  for (let i = 0; i < n; i += 1) {
    const s = g.seed + i * 23.7 + 700
    const u = (i + 0.1 + hash01(s) * 0.8) / n
    const px = g.xStart + 14 + u * (g.w - 28)
    const len = 20 + hash01(s + 1) * hash01(s + 11) * 40 + hash01(s + 12) * 8
    const th = 11 + hash01(s + 2) * 20
    // 傾き: 縦から 0.3〜1.3 rad (寝かせた板も立った板も混ぜる)。先が帯の外へ出ないように詰める。
    let lean = (hash01(s + 3) > 0.45 ? 1 : -1) * (0.3 + hash01(s + 4) * 1.0)
    const maxR = Math.asin(Math.max(-1, Math.min(1, (g.xEnd + 2 - th * 0.5 - px) / len)))
    const maxL = Math.asin(Math.max(-1, Math.min(1, (px - g.xStart - 2 - th * 0.5) / len)))
    lean = Math.max(-maxL, Math.min(maxR, lean))
    out.push({ px, len, th, lean, seed: s, delay: hash01(s + 5) * 28 })
  }
  // 背の高い (縦に近い) 板を奥 (先に描く) へ
  return out.sort((a, b) => b.len * Math.cos(b.lean) - a.len * Math.cos(a.lean))
}

// 床の石畳の色 (実機の画面から: 床の面 ≈ rgb(41,42,56)〜(48,49,66))
const STONE_FACE = 'rgb(56,57,75)'      // 板の表 (床の面と同じ暗い青灰・少しだけ明るい)
const STONE_BODY = 'rgb(33,32,43)'      // 板の厚み・割れた面
const STONE_CUT = 'rgb(20,19,27)'       // 切り口 (いちばん暗い)
const STONE_LIP = 'rgb(96,96,118)'      // 表のふちの光 (ごく一部だけ)

function drawSlab3(ctx: CanvasRenderingContext2D, sl: Slab3, ms: number, red: number, fade: number): void {
  const t = ms - sl.delay
  if (t < 0 || fade <= 0) return
  // 0〜60ms で床から持ち上がり、230ms まで突き立ったまま (わずかに沈む)、その後ゆっくり沈む
  const rise = t < 60 ? easeOut(t / 60) : t < 230 ? 1 - (t - 60) * 0.0004 : Math.max(0, 0.93 - (t - 230) * 0.002)
  const shiver = t < 90 ? Math.sin(t * 0.9 + sl.seed) * (1 - t / 90) * 0.04 : 0
  const ang = -Math.PI / 2 + sl.lean + shiver
  const buried = sl.len * (1 - rise)
  const j = (k: number) => (hash01(sl.seed + k * 1.31) - 0.5)
  const L = sl.len, h = sl.th / 2
  ctx.save()
  ctx.beginPath()
  ctx.rect(sl.px - L - 20, FLOOR - L - 24, L * 2 + 40, L + 24 + 3)   // 床より下は描かない (板は床から持ち上がる)
  ctx.clip()
  ctx.globalAlpha = fade
  ctx.translate(sl.px, FLOOR + 2)
  ctx.rotate(ang)
  ctx.translate(-buried, 0)
  // 外形: 根元はまっすぐ、先は「割れた板の角」(欠けた角と段差)。尖らせない。
  const c1 = 2 + hash01(sl.seed + 20) * h * 0.9      // 上の角の欠け
  const c2 = 2 + hash01(sl.seed + 21) * h * 0.9      // 下の角の欠け
  const notch = hash01(sl.seed + 22) > 0.4
  const outline: Pt[] = [
    [0, -h], [L * (0.45 + j(1) * 0.2), -h + j(2) * 1.5], [L - c1, -h + j(3)],
    [L + j(4) * 1.5, -h + c1 * 0.8],
    ...(notch ? [[L - 3 - hash01(sl.seed + 23) * 5, j(5) * h * 0.6] as Pt, [L + j(6), h * 0.2 + j(7) * 2] as Pt] : [[L + j(5) * 2, j(6) * h * 0.4] as Pt]),
    [L + j(8) * 1.5, h - c2 * 0.8], [L - c2, h + j(9)], [L * (0.5 + j(10) * 0.2), h + j(11) * 1.5], [0, h],
  ]
  ctx.beginPath()
  outline.forEach(([x, y], k) => { if (k === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y) })
  ctx.closePath()
  ctx.fillStyle = STONE_BODY
  ctx.fill()
  ctx.save()
  ctx.clip()
  // 表の面 (床の石畳の上の面) = 片側の広い帯。床と同じ暗い青灰。
  const faceSide = hash01(sl.seed + 9) > 0.5 ? -1 : 1
  const faceW = sl.th * (0.45 + hash01(sl.seed + 24) * 0.25)
  ctx.fillStyle = STONE_FACE
  if (faceSide < 0) ctx.fillRect(-2, -h - 2, L + 4, faceW + 2)
  else ctx.fillRect(-2, h - faceW, L + 4, faceW + 2)
  // 表の面の目地・汚れ (不揃い)
  for (let k = 0; k < 5; k += 1) {
    ctx.globalAlpha = fade * (0.35 + hash01(sl.seed + k * 4.3) * 0.4)
    ctx.fillStyle = k % 2 ? STONE_CUT : 'rgb(44,44,60)'
    const sx = hash01(sl.seed + k * 5.9) * L, sy = j(30 + k) * h * 1.6
    const sr = 1.5 + hash01(sl.seed + k * 6.1) * 3.5
    ctx.beginPath()
    ctx.moveTo(sx - sr, sy); ctx.lineTo(sx + sr * 0.3, sy - sr * 0.6); ctx.lineTo(sx + sr, sy + sr * 0.2); ctx.lineTo(sx - sr * 0.1, sy + sr * 0.5)
    ctx.closePath()
    ctx.fill()
  }
  // 割れた先 (切り口) は暗く・不揃いな面
  ctx.globalAlpha = fade
  ctx.fillStyle = STONE_CUT
  ctx.beginPath()
  ctx.moveTo(L - 4 - hash01(sl.seed + 25) * 6, -h - 1)
  ctx.lineTo(L + 3, -h - 1)
  ctx.lineTo(L + 3, h + 1)
  ctx.lineTo(L - 3 - hash01(sl.seed + 26) * 7, h + 1)
  ctx.lineTo(L - 6 - hash01(sl.seed + 27) * 5, j(28) * h)
  ctx.closePath()
  ctx.fill()
  // 表のふちの光 (ごく一部だけ・長さの 25〜55%)
  const lipFrom = L * (0.1 + hash01(sl.seed + 29) * 0.3)
  const lipLen = L * (0.25 + hash01(sl.seed + 30) * 0.3)
  ctx.globalAlpha = fade * 0.85
  ctx.fillStyle = STONE_LIP
  ctx.fillRect(lipFrom, faceSide < 0 ? -h - 0.5 : h - 1.6, lipLen, 1.8)
  // 根元 (床から切り出されたばかり) は暗く、最初の数コマは下から赤い照り返し
  const gr = ctx.createLinearGradient(buried, 0, buried + 16, 0)
  gr.addColorStop(0, red > 0.02 ? `rgba(200,40,28,${(0.85 * red).toFixed(3)})` : 'rgba(14,13,20,0.8)')
  gr.addColorStop(1, 'rgba(14,13,20,0)')
  ctx.globalAlpha = fade
  ctx.fillStyle = gr
  ctx.fillRect(buried - 2, -h - 2, 20, sl.th + 4)
  ctx.restore()
  ctx.restore()
}

// 帯の床が割れて抜けた暗い穴 (3版と走るひび版で共通): 赤い照り返し・穴・ふちの欠け・奥の赤い粒。帯の切り取りの中で呼ぶ。
function drawBandPit3(ctx: CanvasRenderingContext2D, g: ButtGeom, ms: number, red: number, open: number, stoneFade: number): void {
  // 赤い照り返し (帯の床・最初の数コマ・配布版と同じ強さ)
  if (red > 0.01) {
    lighterBlob(ctx, g.cx, FLOOR + 3, g.w * 0.5, 10, 'rgba(235,44,32,0.95)', 0.62 * red)
    lighterBlob(ctx, g.cx, FLOOR - 14, g.w * 0.4, 24, 'rgba(190,26,24,0.9)', 0.32 * red)
  }
  // 帯の床が割れて抜けた暗い面。上の縁は割れた石畳のふち、下の縁は大きくぎざぎざ。
  ctx.globalAlpha = 0.9 * open * stoneFade
  ctx.fillStyle = '#07060a'
  ctx.beginPath()
  const pitN = 14
  const topY: number[] = []
  for (let i = 0; i <= pitN; i += 1) {
    const x = g.xStart + 3 + (g.w - 6) * (i / pitN) + (i > 0 && i < pitN ? (hash01(g.seed + i * 1.77) - 0.5) * 5 : 0)
    const y = FLOOR + 0.5 - hash01(g.seed + i * 2.13) * 2.5
    topY.push(y)
    ctx.lineTo(x, y)
  }
  for (let i = pitN; i >= 0; i -= 1) {
    const edge = i === 0 || i === pitN
    const x = g.xStart + 3 + (g.w - 6) * (i / pitN) + (hash01(g.seed + i * 4.4) - 0.5) * 6
    const deep = hash01(g.seed + i * 3.21)
    ctx.lineTo(x, edge ? FLOOR + 2 : FLOOR + 3 + deep * deep * 12 + (i % 2) * 2)
  }
  ctx.closePath()
  ctx.fill()
  // 穴のふち = 割れた石畳の欠け (床と同じ色の不揃いな小片)
  for (let i = 0; i < 9; i += 1) {
    const s = g.seed + i * 7.9 + 2100
    const x = g.xStart + 6 + hash01(s) * (g.w - 12)
    const w = 3 + hash01(s + 1) * 9
    ctx.globalAlpha = 0.85 * open * stoneFade
    ctx.fillStyle = hash01(s + 2) > 0.7 ? STONE_LIP : STONE_FACE
    ctx.beginPath()
    ctx.moveTo(x - w * 0.5, FLOOR + 1)
    ctx.lineTo(x - w * 0.2, FLOOR - 0.5 - hash01(s + 3) * 2)
    ctx.lineTo(x + w * 0.5, FLOOR - hash01(s + 4) * 1.5)
    ctx.lineTo(x + w * 0.4, FLOOR + 1.5)
    ctx.closePath()
    ctx.fill()
  }
  ctx.globalAlpha = 1
  for (let i = 0; i < 6; i += 1) {
    const x = g.xStart + 10 + hash01(g.seed + i * 6.6) * (g.w - 20)
    glowMote(ctx, x, FLOOR + 4, 5 + hash01(g.seed + i * 1.4) * 6, 'rgb(190,34,24)',
      (0.2 + 0.45 * red) * (1 - smooth01(60, 360, ms)) * open * stoneFade)
  }
}

function drawEruptionV3(ctx: CanvasRenderingContext2D, g: ButtGeom, ms: number, activeMs: number): void {
  const red = redFlash(ms)
  const endMs = activeMs + V3_LINGER_MS
  const stoneFade = 1 - smooth01(activeMs + 40, endMs, ms)
  const dustFade = 1 - smooth01(activeMs - 20, endMs, ms)
  const open = easeOut(ms / 40)

  // 石突の着点から帯までの暗い細いひび (赤なし・破片なし・すぐ消える)
  const hair = (1 - smooth01(90, 260, ms)) * easeOut((ms + RUN_LEAD_MS) / RUN_MS)
  if (hair > 0.01) {
    const total = Math.abs(g.inner - g.buttX)
    const reach = total * easeOut((ms + RUN_LEAD_MS) / RUN_MS)
    ctx.save()
    ctx.globalAlpha = 0.75 * (1 - smooth01(90, 260, ms))
    ctx.fillStyle = '#0b0a0f'
    ctx.beginPath()
    const n = Math.max(2, Math.ceil(reach / 6))
    const pts: Pt[] = []
    for (let i = 0; i <= n; i += 1) {
      const s = Math.min(reach, i * 6)
      pts.push([g.buttX + g.dir * s, crackY(g, s) - 1])
    }
    pts.forEach(([x, y], i) => { if (i === 0) ctx.moveTo(x, y - 0.6); else ctx.lineTo(x, y - 0.6 - (i % 3 === 1 ? 0.5 : 0)) })
    for (let i = pts.length - 1; i >= 0; i -= 1) {
      const u = i / Math.max(1, pts.length - 1)
      ctx.lineTo(pts[i]![0], pts[i]![1] + 0.6 + u * 1.1 + (hash01(g.seed + i * 3.3) > 0.7 ? 0.8 : 0))
    }
    ctx.closePath()
    ctx.fill()
    ctx.restore()
  }

  // ここから先 (危険に見える物) は帯の横範囲 ±V3_BLEED の中だけに描く
  ctx.save()
  ctx.beginPath()
  ctx.rect(g.xStart - V3_BLEED, FLOOR - 140, g.w + V3_BLEED * 2, 160)
  ctx.clip()

  drawBandPit3(ctx, g, ms, red, open, stoneFade)

  // 土煙 (後ろ・脇役): 板の根元から湧く。帯の中で高さ 60px まで。
  for (let i = 0; i < 18; i += 1) {
    const s = g.seed + i * 9.71 + 1300
    const t = ms - hash01(s) * 40
    if (t < 0) continue
    const grow = easeOut(t / 260)
    const rx = (7 + grow * 14) * (0.7 + hash01(s + 4) * 0.5)
    const x = g.xStart + rx * 0.6 + hash01(s + 1) * Math.max(1, g.w - rx * 1.2)
    const y = FLOOR - 4 - grow * (6 + hash01(s + 3) * 48)
    const tone = hash01(s + 6)
    softBlob(ctx, x, y, rx, rx * (0.5 + hash01(s + 5) * 0.3), (hash01(s + 7) - 0.5) * 0.8,
      tone > 0.6 ? 'rgba(122,118,132,0.95)' : 'rgba(86,83,98,0.95)', 0.58 * dustFade * Math.min(1, t / 40 + 0.3))
  }

  // 主役: 床から割れて持ち上がった石畳の板
  const slabs = slabs3Of(g)
  for (const sl of slabs) drawSlab3(ctx, sl, ms, red, stoneFade)

  // 板の根元の土・石くず (床から切り出された感じ)
  for (const sl of slabs) {
    const t = ms - sl.delay
    if (t < 0) continue
    const k = easeOut(t / 80)
    for (let i = 0; i < 5; i += 1) {
      const s = sl.seed + i * 3.7 + 50
      const x = sl.px + (hash01(s) - 0.5) * sl.th * 1.6
      const r = 1.4 + hash01(s + 1) * 3.2
      stoneChip(ctx, x, FLOOR + 0.5 - hash01(s + 2) * 2 * k, r * k, hash01(s + 3) * 3, s, 0.95 * stoneFade, 'rgb(24,23,31)', 'rgb(60,60,78)')
    }
    softBlob(ctx, sl.px, FLOOR - 2 - k * 4, 6 + k * 8, 4 + k * 3, 0, 'rgba(70,67,80,0.9)', 0.5 * k * dustFade)
  }

  // 破片 (脇役): 帯の中から上がって帯の中へ落ちる。床と同じ暗い石の色。
  for (let i = 0; i < 38; i += 1) {
    const s = g.seed + i * 5.77 + 1700
    const t = ms - hash01(s) * 35
    if (t < 0) continue
    const big = hash01(s + 4) * hash01(s + 5)
    const r = 1.1 + big * 6
    const vy = 0.2 + hash01(s + 2) * 0.34 * (1 - big * 0.4)
    const vx = (hash01(s + 1) - 0.5) * 0.1
    const flight = (2 * vy) / GRAV
    const tt = Math.min(t, flight)
    const x0 = g.xStart + 8 + hash01(s + 3) * (g.w - 16)
    const x = x0 + vx * tt
    const y = FLOOR - 1 - Math.max(0, vy * tt - 0.5 * GRAV * tt * tt) + (t >= flight ? 1.5 : 0)
    stoneChip(ctx, x, y, r, (hash01(s + 6) - 0.5) * 0.03 * tt, s, 0.95 * stoneFade, 'rgb(28,27,36)', hash01(s + 7) > 0.75 ? 'rgb(92,92,112)' : 'rgb(58,58,76)')
  }

  // 土煙 (手前の薄い層・低い)
  for (let i = 0; i < 8; i += 1) {
    const s = g.seed + i * 13.3 + 1900
    const t = ms - hash01(s) * 30
    if (t <= 0) continue
    const grow = easeOut(t / 220)
    const rx = (6 + grow * 11) * (0.7 + hash01(s + 3) * 0.5)
    const x = g.xStart + rx * 0.6 + hash01(s + 1) * Math.max(1, g.w - rx * 1.2)
    const y = FLOOR - 2 - grow * (4 + hash01(s + 2) * 18)
    softBlob(ctx, x, y, rx, rx * (0.45 + hash01(s + 4) * 0.3), (hash01(s + 5) - 0.5) * 0.7,
      'rgba(132,128,140,0.9)', 0.36 * dustFade)
  }
  ctx.restore()
}

// ---------------------------------------------------------------- 走るひび (2026-10-20・既定)
// 作者の選択 = 床技7つの見本の C「走るひび」(docs/research/2026-10-19-floor-fx-mihon.md)。噴き出す物は結晶でなく「石と砂ぼこり」。
// 作者の物差し:「名前通り作りたい」= 石突で床を突き返す → 床が割れて石が突き上がり、砂ぼこりが立つ。
//   ① 石突の着点から帯の手前まで、暗いひびが約2コマで走る (安全な所に赤・破片・煙は出さない＝3版の約束のまま)。
//   ② 帯の中だけ (±V3_BLEED): 割れた床の石が突き上がり、石塊と小石が舞い、砂ぼこりが主人公の背丈ほど立つ。
//   ③ 当たりの後は石が沈み、低い砂ぼこりが少し残って晴れる。
// 絵の部品 = Codex が1枚に描いた物を切り出した atlas (元絵 そざい/concept/knight-butt-rift-1020/・切り出しの記録は LUNAIRE-notes/assets/2026-10-20-knight-butt-rift/)。
// 動きはコード。絵が読めていない間は3版で描く。

const RIFT_LINGER_MS = 380
type RiftRect = readonly [number, number, number, number]
export const RIFT_ATLAS: Record<string, RiftRect> = {
  slab_0: [0, 0, 48, 159], slab_1: [50, 0, 44, 116], slab_2: [96, 0, 51, 165], slab_3: [149, 0, 49, 128],
  chunk_0: [200, 0, 29, 39], chunk_1: [231, 0, 19, 27], chunk_2: [252, 0, 24, 24], chunk_3: [278, 0, 21, 36],
  chunk_4: [301, 0, 18, 22], chunk_5: [321, 0, 30, 38], chunk_6: [353, 0, 18, 20], chunk_7: [373, 0, 19, 34],
  grit_0: [394, 0, 12, 16], grit_1: [408, 0, 10, 12], grit_2: [420, 0, 12, 14], grit_3: [434, 0, 10, 11],
  grit_4: [446, 0, 11, 12], grit_5: [459, 0, 11, 14], grit_6: [472, 0, 12, 16], grit_7: [486, 0, 9, 11],
  grit_8: [497, 0, 11, 12], grit_9: [0, 167, 10, 13], grit_10: [12, 167, 10, 13],
  col_0: [24, 167, 178, 224], col_1: [204, 167, 138, 218],
  low_0: [0, 393, 193, 98], low_1: [195, 393, 145, 100], low_2: [0, 495, 195, 100],
}
const RIFT_SRC = '/sprites/fx/hazard/knight_butt_rift_atlas.png'
let riftImg: CanvasImageSource | null = null
let riftLoading = false
/** 部品の絵の読み込み (走るひびの時だけ・1回)。 */
export function preloadKnightButtRiftAtlas(): void {
  if (riftImg || riftLoading || typeof Image === 'undefined') return
  riftLoading = true
  const img = new Image()
  img.onload = () => { riftImg = img }
  img.onerror = () => { riftLoading = false }
  img.src = RIFT_SRC
}
/** テスト用 (偽の絵を差す)。 */
export function setKnightButtRiftAtlasForTest(img: CanvasImageSource | null): void { riftImg = img }

// 部品1つを、下端中央 (ax, ay) を基準に回して描く。dw×dh はゲームの px。
function riftPart(
  ctx: CanvasRenderingContext2D, img: CanvasImageSource, name: string,
  ax: number, ay: number, dw: number, dh: number, rot: number, a: number, flip = false,
): void {
  if (a <= 0.01 || dw <= 0.5 || dh <= 0.5) return
  const r = RIFT_ATLAS[name]!
  ctx.save()
  ctx.globalAlpha = Math.min(1, a)
  ctx.translate(ax, ay)
  if (rot) ctx.rotate(rot)
  if (flip) ctx.scale(-1, 1)
  ctx.drawImage(img, r[0], r[1], r[2], r[3], -dw / 2, -dh, dw, dh)
  ctx.restore()
}

type RiftSlab = { name: string; px: number; h: number; w: number; lean: number; delay: number; flip: boolean; seed: number }
function riftSlabsOf(g: ButtGeom): RiftSlab[] {
  // 5本。4枚の絵を使い回す (1本は裏返し)。背丈・傾き・位置をばらし、等間隔に並べない。
  // 割れ目は帯の石突側から来るので、石突側の石が数ミリ秒だけ先に上がる (当たりは帯の全体が 0ms から・時間は変えない)。
  const order = ['slab_2', 'slab_0', 'slab_3', 'slab_1', 'slab_0']
  const out: RiftSlab[] = []
  for (let i = 0; i < order.length; i += 1) {
    const s = g.seed + i * 17.3 + 4100
    const name = order[i]!
    const r = RIFT_ATLAS[name]!
    const u = (i + 0.15 + hash01(s) * 0.7) / order.length
    const px = g.xStart + 12 + u * (g.w - 24)
    const h = (i === 0 ? 62 : i === 2 ? 52 : 34 + hash01(s + 1) * 18) * (0.92 + hash01(s + 2) * 0.16)
    const w = h * (r[2] / r[3])
    // 傾き: 縦から ±0.08〜0.42 rad。先が帯の外へ出ないように詰める。
    let lean = (hash01(s + 3) > 0.5 ? 1 : -1) * (0.08 + hash01(s + 4) * 0.34)
    const room = (side: number) => Math.asin(Math.max(-1, Math.min(1, (side - w * 0.5) / h)))
    lean = Math.max(-room(px - g.xStart + 2), Math.min(room(g.xEnd + 2 - px), lean))
    const fromInner = Math.abs(px - g.inner) / Math.max(1, g.w)
    out.push({ name, px, h, w, lean, delay: fromInner * 18 + hash01(s + 5) * 6, flip: i === 4, seed: s })
  }
  // 背の高い石を奥 (先に描く) へ
  return out.sort((a, b) => b.h - a.h)
}

// 石の立ち上がり: 0〜42ms で床から突き上がって少し行き過ぎ、当たりの間は立ったまま、その後沈む。
function riftRise(t: number, activeMs: number): number {
  if (t < 0) return 0
  if (t < 42) return easeOut(t / 42) * 1.07
  if (t < 70) return 1.07 - 0.07 * smooth01(42, 70, t)
  const sinkFrom = activeMs - 10
  return t < sinkFrom ? 1 - (t - 70) * 0.00012 : Math.max(0, 1 - (t - 70) * 0.00012 - (t - sinkFrom) * 0.0028)
}

function drawRiftRun(ctx: CanvasRenderingContext2D, g: ButtGeom, ms: number): void {
  // 石突の着点から帯の手前までの、暗いひび。縁の欠けが所々明るい (赤・破片・煙は出さない)。
  const run = easeOut((ms + RUN_LEAD_MS) / RUN_MS)
  const fade = 1 - smooth01(220, 520, ms)   // 10-20 撮影: 120-340 では走った跡が読めないので長く残す
  if (run <= 0.01 || fade <= 0.01) return
  const total = Math.abs(g.inner - g.buttX)
  const reach = total * run
  const n = Math.max(2, Math.ceil(reach / 5))
  const pts: Pt[] = []
  for (let i = 0; i <= n; i += 1) {
    const sd = Math.min(reach, i * 5)
    pts.push([g.buttX + g.dir * sd, crackY(g, sd) - 1])
  }
  ctx.save()
  ctx.globalAlpha = 0.92 * fade
  ctx.fillStyle = '#09080d'
  ctx.beginPath()
  // 10-20 撮影: 幅 1.6 では主人公の足もとに埋もれたので、石突の側 2.4 → 帯の側 4.6 に太らせる
  pts.forEach(([x, y], i) => { if (i === 0) ctx.moveTo(x, y - 1.2); else ctx.lineTo(x, y - 1.2 - (i / Math.max(1, pts.length - 1)) * 0.8 - (i % 3 === 1 ? 0.9 : 0)) })
  for (let i = pts.length - 1; i >= 0; i -= 1) {
    const u = i / Math.max(1, pts.length - 1)
    ctx.lineTo(pts[i]![0], pts[i]![1] + 1.2 + u * 1.8 + (hash01(g.seed + i * 3.3) > 0.7 ? 1.3 : 0))
  }
  ctx.closePath()
  ctx.fill()
  // ひびの縁の欠け (床の石の明るいふちが、ところどころ割れて見える)
  ctx.fillStyle = STONE_LIP
  for (let i = 1; i < pts.length; i += 1) {
    if (hash01(g.seed + i * 5.9) < 0.6) continue
    const [x, y] = pts[i]!
    const w = 2 + hash01(g.seed + i * 2.2) * 3
    ctx.globalAlpha = 0.8 * fade
    ctx.fillRect(x - w / 2, y - 3, w, 1.3)
  }
  // 割れ目の短い枝 (帯へ近いほど多い)
  ctx.fillStyle = '#09080d'
  for (let i = 2; i < pts.length; i += 1) {
    const u = i / Math.max(1, pts.length - 1)
    if (hash01(g.seed + i * 7.1) > 0.25 + u * 0.3) continue
    const [x, y] = pts[i]!
    const len = 3 + hash01(g.seed + i * 1.3) * 6
    const up = hash01(g.seed + i * 4.7) > 0.5 ? -1 : 1
    ctx.globalAlpha = 0.7 * fade
    ctx.beginPath()
    ctx.moveTo(x, y)
    ctx.lineTo(x + g.dir * len * 0.7, y + up * len * 0.35)
    ctx.lineTo(x + g.dir * len * 0.2, y + 0.9)
    ctx.closePath()
    ctx.fill()
  }
  ctx.restore()
}

function drawEruptionRift(ctx: CanvasRenderingContext2D, g: ButtGeom, ms: number, activeMs: number): void {
  const img = riftImg
  if (!img) { preloadKnightButtRiftAtlas(); drawEruptionV3(ctx, g, ms, activeMs); return }
  const red = redFlash(ms)
  const endMs = activeMs + RIFT_LINGER_MS
  const stoneFade = 1 - smooth01(activeMs + 30, activeMs + 230, ms)
  const colFade = 1 - smooth01(activeMs - 30, activeMs + 200, ms)
  const lowFade = 1 - smooth01(activeMs + 40, endMs, ms)
  const open = easeOut(ms / 40)

  drawRiftRun(ctx, g, ms)

  // ここから先 (危険に見える物) は帯の横範囲 ±V3_BLEED の中だけに描く
  ctx.save()
  ctx.beginPath()
  ctx.rect(g.xStart - V3_BLEED, FLOOR - 150, g.w + V3_BLEED * 2, 170)
  ctx.clip()
  drawBandPit3(ctx, g, ms, red, open, stoneFade)

  // 砂ぼこりの柱 (奥): 割れ目から噴き上がって主人公の背丈ほど。当たりが終わると上へほどけて薄れる。
  const cols: [string, number, number][] = [['col_0', -0.1, 0.8], ['col_1', 0.17, 0.64]]
  for (let i = 0; i < cols.length; i += 1) {
    const [name, off, wk] = cols[i]!
    const r = RIFT_ATLAS[name]!
    const s = g.seed + i * 31.7 + 5200
    const t = ms + 10 - i * 14 - hash01(s) * 8
    if (t < 0) continue
    const grow = easeOut(t / 170)
    const w = g.w * wk * (0.5 + 0.5 * grow)
    const h = (g.w * wk) * (r[3] / r[2]) * (0.22 + 0.9 * grow)
    const lift = (1 - colFade) * 14
    riftPart(ctx, img, name, g.cx + g.dir * off * g.w, FLOOR + 6 - lift, w * (1 + (1 - colFade) * 0.12), h, 0,
      colFade * Math.min(1, t / 24 + 0.35), g.dir < 0)
  }

  // 主役: 割れて突き上がる床の石
  const slabs = riftSlabsOf(g)
  for (const sl of slabs) {
    const t = ms + 12 - sl.delay   // 当たりの 0ms で石突側の石はもう床を割って出始めている
    const rise = riftRise(t, activeMs)
    if (rise <= 0) continue
    const shake = t < 90 ? Math.sin(t * 0.8 + sl.seed) * (1 - t / 90) * 0.035 : 0
    ctx.save()
    ctx.beginPath()
    ctx.rect(g.xStart - V3_BLEED, FLOOR - 150, g.w + V3_BLEED * 2, 150 + 4)   // 床より下は描かない (石は床から突き上がる)
    ctx.clip()
    const sink = sl.h * (1 - Math.min(1, rise))
    const over = Math.max(0, rise - 1) * sl.h
    ctx.translate(sl.px, FLOOR + 4)
    ctx.rotate(sl.lean + shake)
    riftPart(ctx, img, sl.name, 0, sink - over, sl.w, sl.h + 4, 0, stoneFade, sl.flip)
    // 根元の赤い照り返し (最初の数コマ)
    if (red > 0.02) {
      ctx.globalCompositeOperation = 'lighter'
      softBlob(ctx, 0, -2, sl.w * 0.8, 6, 0, 'rgba(210,40,28,0.9)', 0.55 * red * stoneFade)
    }
    ctx.restore()
    // 根元の影 (床から切り出された所)
    softBlob(ctx, sl.px, FLOOR + 1, sl.w * 0.9, 3.5, 0, 'rgba(8,7,12,0.95)', 0.7 * stoneFade * Math.min(1, rise))
  }

  // 石塊と小石: 帯の中から上がって帯の中へ落ち、床で止まる。回りながら飛ぶ。
  const flyN = 26
  for (let i = 0; i < flyN; i += 1) {
    const s = g.seed + i * 6.13 + 5600
    const t = ms + 8 - hash01(s) * 34
    if (t < 0) continue
    const isChunk = i < 10
    const name = isChunk ? `chunk_${Math.floor(hash01(s + 1) * 8)}` : `grit_${Math.floor(hash01(s + 1) * 11)}`
    const r = RIFT_ATLAS[name]!
    const size = isChunk ? 7 + hash01(s + 2) * 8 : 2.6 + hash01(s + 2) * 3.4
    const k = size / Math.max(r[2], r[3])
    const vy = isChunk ? 0.26 + hash01(s + 3) * 0.2 : 0.3 + hash01(s + 3) * 0.32
    const flight = (2 * vy) / GRAV
    const tt = Math.min(t, flight)
    const x0 = g.xStart + 8 + hash01(s + 4) * (g.w - 16)
    let vx = (hash01(s + 5) - 0.5) * 0.14
    const xLand = x0 + vx * flight
    if (xLand < g.xStart + 4 || xLand > g.xEnd - 4) vx = -vx
    const x = x0 + vx * tt
    const y = FLOOR + 2 - Math.max(0, vy * tt - 0.5 * GRAV * tt * tt)
    const spin = (hash01(s + 6) - 0.5) * 0.03 * tt
    riftPart(ctx, img, name, x, y, r[2] * k, r[3] * k, spin, stoneFade)   // 下端を床に置く (床より下へ沈ませない)
  }

  // 低い砂ぼこり (手前): 根元に広がり、当たりの後も少し残って晴れる。
  const lows: [string, number, number][] = [['low_0', -0.24, 0.5], ['low_1', 0.02, 0.42], ['low_2', 0.26, 0.5]]
  for (let i = 0; i < lows.length; i += 1) {
    const [name, off, wk] = lows[i]!
    const r = RIFT_ATLAS[name]!
    const s = g.seed + i * 11.9 + 6100
    const t = ms - 20 - hash01(s) * 30
    if (t < 0) continue
    const grow = easeOut(t / 160)
    const w = g.w * wk * (0.45 + 0.55 * grow)
    const h = g.w * wk * (r[3] / r[2]) * (0.4 + 0.6 * grow)
    const drift = (1 - lowFade) * 6 * Math.sign(off || 1)
    riftPart(ctx, img, name, g.cx + g.dir * off * g.w + drift, FLOOR + 5, w, h, 0, 0.95 * lowFade, hash01(s + 1) > 0.5)
  }
  ctx.restore()
}

if (buttFx === 'rift' && typeof window !== 'undefined') preloadKnightButtRiftAtlas()
